import { Field, inputCls } from "@/components/Kit";

export const SEX_OPTS = ["Male", "Female", "Transgender person"];
export const RELIGION_OPTS = ["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jain", "Other"];
export const PLACE_OPTS = [
  { v: "hospital_institution", l: "Hospital / Institution" },
  { v: "house", l: "House" },
  { v: "other", l: "Other place" },
];
export const ATTENTION_OPTS = [
  { v: "institutional_government", l: "Institutional — Government" },
  { v: "institutional_private", l: "Institutional — Private / Non-Government" },
  { v: "doctor_nurse_midwife", l: "Doctor, Nurse or Trained Midwife" },
  { v: "traditional_birth_attendant", l: "Traditional Birth Attendant" },
  { v: "relatives_others", l: "Relatives or others" },
];
export const DELIVERY_OPTS = [
  { v: "natural", l: "Natural" },
  { v: "caesarean", l: "Caesarean" },
  { v: "forceps_vacuum", l: "Forceps / Vacuum" },
];
export const MED_ATTN_OPTS = [
  { v: "institutional", l: "Institutional" },
  { v: "other_than_institution", l: "Medical attention other than Institution" },
  { v: "no_medical_attention", l: "No Medical attention" },
];
export const MANNER_OPTS = [
  { v: "natural", l: "Natural" },
  { v: "accident", l: "Accident" },
  { v: "suicide", l: "Suicide" },
  { v: "homicide", l: "Homicide" },
  { v: "pending_investigation", l: "Pending investigation" },
];

export const EMPTY_ADDRESS = {
  house_no: "", locality: "", ward_number: "", town_or_village: "",
  sub_district: "", district: "", state_ut: "", pin_code: "",
};
export const EMPTY_REGISTRAR = {
  registration_no: "", registration_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }), registration_unit: "",
  town_or_village: "", sub_district: "", district: "", remarks: "",
};

/** Immutable deep-set for dotted paths, e.g. setPath(form, "father.name", "x"). */
export function setPath(obj, path, value) {
  const keys = path.split(".");
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let node = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    node[k] = Array.isArray(node[k]) ? [...node[k]] : { ...(node[k] || {}) };
    node = node[k];
  }
  node[keys[keys.length - 1]] = value;
  return clone;
}

export const Sec = ({ n, title, desc, children }) => (
  <section className="rounded-lg border border-slate-200 p-4 sm:p-5">
    <div className="mb-4 flex items-baseline gap-2">
      {n != null && <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-slate-900 text-[11px] font-bold text-white">{n}</span>}
      <div>
        <h4 className="font-ui text-sm font-bold text-slate-900">{title}</h4>
        {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
      </div>
    </div>
    {children}
  </section>
);

export const Grid = ({ children, cols = 2 }) => (
  <div className={`grid grid-cols-1 gap-4 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>{children}</div>
);

export const Txt = ({ label, value, onChange, testid, type = "text", placeholder = "" }) => (
  <Field label={label}>
    <input type={type} className={inputCls} data-testid={testid} value={value ?? ""}
      placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </Field>
);

export const Num = ({ label, value, onChange, testid, step }) => (
  <Field label={label}>
    <input type="number" step={step} className={inputCls} data-testid={testid} value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
  </Field>
);

export const Sel = ({ label, value, onChange, options, testid, placeholder = "Select…" }) => (
  <Field label={label}>
    <select className={inputCls} data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.v;
        const l = typeof o === "string" ? o : o.l;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  </Field>
);

/** Full 8-part statutory address block. */
export const AddressFields = ({ value = EMPTY_ADDRESS, onChange, prefix }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <Grid cols={3}>
      <Txt label="House No." value={value.house_no} onChange={set("house_no")} testid={`${prefix}-house`} />
      <Txt label="Locality" value={value.locality} onChange={set("locality")} testid={`${prefix}-locality`} />
      <Txt label="Ward number" value={value.ward_number} onChange={set("ward_number")} testid={`${prefix}-ward`} />
      <Txt label="Town or Village" value={value.town_or_village} onChange={set("town_or_village")} testid={`${prefix}-town`} />
      <Txt label="Sub-district" value={value.sub_district} onChange={set("sub_district")} testid={`${prefix}-subdist`} />
      <Txt label="District" value={value.district} onChange={set("district")} testid={`${prefix}-district`} />
      <Txt label="State / UT" value={value.state_ut} onChange={set("state_ut")} testid={`${prefix}-state`} />
      <Txt label="PIN Code" value={value.pin_code} onChange={set("pin_code")} testid={`${prefix}-pin`} />
    </Grid>
  );
};

export const RegistrarFields = ({ value = EMPTY_REGISTRAR, onChange, prefix }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <Grid cols={3}>
      <Txt label="Registration No." value={value.registration_no} onChange={set("registration_no")} testid={`${prefix}-regno`} />
      <Txt label="Registration Date" type="date" value={value.registration_date} onChange={set("registration_date")} testid={`${prefix}-regdate`} />
      <Txt label="Registration Unit" value={value.registration_unit} onChange={set("registration_unit")} testid={`${prefix}-regunit`} />
      <Txt label="Town / Village" value={value.town_or_village} onChange={set("town_or_village")} testid={`${prefix}-regtown`} />
      <Txt label="Sub-District" value={value.sub_district} onChange={set("sub_district")} testid={`${prefix}-regsub`} />
      <Txt label="District" value={value.district} onChange={set("district")} testid={`${prefix}-regdist`} />
      <Field label="Remarks" className="sm:col-span-3">
        <textarea className={inputCls} rows={2} data-testid={`${prefix}-remarks`} value={value.remarks ?? ""}
          onChange={(e) => set("remarks")(e.target.value)} />
      </Field>
    </Grid>
  );
};
